import React from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {Close, Download, Folder} from '../../../icons';
import {P} from '../../../layout/Text/Text';

import {useFileItem} from './useFileItem';
import {getIconSize, getItemPadding} from './utils';

import type {FileProgress} from './useFileItem';

/**
 * The `ComponentProps` interface defines the properties required by the `FileItem` component.
 * It includes details about the uploaded file, the current progress of the upload, error handling,
 * and styling. These properties allow the `FileItem` to properly display the file's upload state,
 * provide user feedback, and allow deletion of the uploaded file.
 */
export interface FileItemProps {
    /**
     * The index of the asset in the upload list. This is used to identify the asset in the list.
     */
    index: number;
    /**
     * The name to display for the file.
     */
    name: string;
    /**
     * Callback function triggered when the delete action is performed.
     * This function is used to remove the uploaded file from the UI or state.
     */
    onDelete?(index: number): void;
    /**
     * Callback function triggered when the download action is performed.
     */
    onDownload?(index: number): void;
    /**
     * An object containing the current progress of the asset upload.
     * This object includes the upload status and progress percentage.
     */
    progressData?: FileProgress;
    /**
     * The size of the file item component, which can be either 'large' or 'small'.
     * This prop determines the overall dimensions and styling of the file input,
     * allowing for different visual presentations based on the specified size.
     */
    size?: 'large' | 'small';
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'FileItem'
     */
    testId?: string;
}

/**
 * A component representing an asset item in an asset upload list.
 * This component displays the asset's name, upload progress, and error messages if applicable.
 * It provides a delete action that allows users to remove the asset from the list.
 *
 * @param props              The component props.
 * @param props.file         The file object representing the asset.
 * @param props.index        The index of the asset in the upload list.
 * @param props.onDelete     A callback function triggered when the asset is deleted (default: empty function).
 * @param props.progressData An object containing upload progress data.
 * @param props.testId       A unique test identifier for the component (default: `'FileItem'`).
 * @param props.value        Additional metadata related to the asset.
 * @returns A React component rendering an asset item with progress and error handling.
 *
 * @example
 * ```tsx
 * <FileItem
 *   file={file}
 *   index={0}
 *   progressData={{ progress: 50, status: "uploading" }}
 *   onDelete={() => console.log("Asset deleted")}
 * />
 * ```
 */
const FileItem = observer(({
    index,
    name,
    onDelete,
    onDownload,
    progressData,
    size = 'large',
    testId = 'FileItem'
}: FileItemProps) => {
    const {colors, error, handleDelete, handleDownload, progress, showDeleteButton, showDownloadButton} = useFileItem(
        index,
        onDelete,
        onDownload,
        progressData
    );

    return (
        <Wrapper $size={size} data-cy={testId}>
            <FolderIcon color1={colors.neutral100} height={getIconSize(size)} width={getIconSize(size)} />
            <InfoWrapper>
                <FileName $size={size === 'small' ? 'small' : 'medium'} data-cy={`${testId}-file-name`}>
                    {name}
                </FileName>
                {(progress && progress !== 'done' && !error) && (
                    <>
                        <Spacer y={2} />
                        <ProgressBar data-cy={`${testId}-progress-bar`}>
                            <ProgressBarFill data-cy={`${testId}-progress-bar-fill`} style={{width: `${progress}%`}} />
                        </ProgressBar>
                    </>
                )}
                {error && (
                    <>
                        <Spacer y={2} />
                        <P $size="small" data-cy={`${testId}-error-message`}>
                            <Color $color={colors.error}>{error}</Color>
                        </P>
                    </>
                )}
            </InfoWrapper>
            {(showDownloadButton || showDeleteButton) && (<Divider />)}
            {showDownloadButton && (
                <ActionWrapper data-cy={`${testId}-download-button`} onClick={handleDownload}>
                    <Download color1={colors.primary} height={24} width={24} />
                </ActionWrapper>
            )}
            {showDeleteButton && (
                <ActionWrapper data-cy={`${testId}-delete-button`} onClick={handleDelete}>
                    <Close color1={colors.primary} height={24} width={24} />
                </ActionWrapper>
            )}
        </Wrapper>
    );
});

FileItem.displayName = 'FileItem';

export {FileItem};

interface WrapperProps {
    $size: 'large' | 'small';
}

const Wrapper = styled.div<WrapperProps>`
    align-items: center;
    border: 1px solid ${({theme}) => theme.colors.neutral40};
    border-radius: 8px;
    display: flex;
    gap: ${spacing(2)};
    padding: ${({$size}) => getItemPadding($size)};
    width: 100%;
`;

const FolderIcon = styled(Folder)`
    flex: 0 0 25px;
`;

const InfoWrapper = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    margin-inline-end: ${spacing(11)};
    min-width: 0;
`;

const FileName = styled(P)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ProgressBar = styled.div`
    background-color: ${({theme}) => theme.colors.neutral20};
    border-radius: 2px;
    height: 0.4rem;
    overflow: hidden;
    width: 100%;
`;

const ProgressBarFill = styled.div`
    background-color: ${({theme}) => theme.colors.primary};
    border-radius: 2px;
    height: 100%;
    transition: width 0.2s ease-in-out;
`;

const Divider = styled.div`
    background-color: ${({theme}) => theme.colors.neutral40};
    height: 1.6rem;
    width: 0.1rem;
`;

const ActionWrapper = styled.div`
    align-items: center;
    cursor: pointer;
    display: flex;
    flex: 0 0 20px;
    justify-content: center;
    padding: ${spacing(1)};

    &:hover {
        background-color: ${({theme}) => theme.colors.neutral20};
        border-radius: 4px;
    }
`;